<template>
  <div id="individual-display-data" class="dialog-container">
    <overlay-loading-progress
      :LoadingFlag="DeleteLoadingFlag"
    ></overlay-loading-progress>

    <!-- Create Plan Dialog -->
    <v-dialog
      v-model="createPlanFlag"
      v-if="createPlanFlag"
      persistent
      max-width="75%"
    >
      <jci-program-plans-create
        v-if="createPlanFlag"
        :record="record"
        @hideDialog="hideCreatePlan($event)"
      ></jci-program-plans-create>
    </v-dialog>

    <!-- Edit Plan Dialog -->
    <v-dialog
      v-model="editPlanFlag"
      v-if="editPlanFlag"
      persistent
      max-width="75%"
    >
      <jci-program-plans-edit
        v-if="editPlanFlag"
        :record="selectedData"
        @hideDialog="hideEditPlan($event)"
      ></jci-program-plans-edit>
    </v-dialog>

    <!-- Info Dialog -->
    <v-dialog v-model="dialog" scrollable max-width="400px">
      <v-card class="info-dialog-card">
        <v-card-title class="info-dialog-header">
          <v-icon color="primary" size="24" class="mr-2"
            >mdi-information</v-icon
          >
          {{ dialogTitle }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="info-dialog-content">
          <p class="text-body2" v-html="dialogContent"></p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="dialog = false"
            class="close-info-btn"
          >
            CLOSE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Dialog Card -->
    <v-card class="dialog-card elevation-24">
      <!-- Professional Header -->
      <div class="dialog-header">
        <div class="header-left">
          <v-icon size="36" color="white" class="header-icon-badge"
            >mdi-clipboard-text</v-icon
          >
          <div class="header-text-section">
            <h2 class="header-title">{{ pageTitle }}</h2>
            <p class="header-subtitle">Manage program plans and activities</p>
          </div>
        </div>
        <div class="header-right">
          <v-btn
            icon
            dark
            @click="refreshPageData"
            class="header-action-btn"
            v-tooltip.left="'Refresh'"
          >
            <v-icon size="24">mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            icon
            dark
            @click="closePrompt"
            class="header-action-btn"
            v-tooltip.left="'Close'"
          >
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Content Area -->
      <v-sheet
        id="scrolling-techniques-7"
        class="dialog-content-sheet overflow-y-auto"
        max-height="500"
      >
        <!-- Loading State -->
        <div v-if="LoadingFlag" class="text-center py-12">
          <v-progress-circular
            :size="70"
            :width="7"
            color="primary"
            indeterminate
          ></v-progress-circular>
          <p class="mt-6 text-h6 primary--text font-weight-600">
            Loading plans...
          </p>
        </div>

        <!-- Main Content -->
        <v-card-text class="dialog-body-content pa-6" v-if="!LoadingFlag">
          <!-- Add Button Section -->
          <div class="add-button-section mb-6">
            <v-btn
              v-if="TableOptions.CreateButtonFlag"
              @click="createPlanFlag = true"
              color="#667eea"
              elevation="6"
              rounded
              height="45"
              class="add-plan-btn white--text"
            >
              <v-icon size="22" class="mr-2">mdi-plus-circle</v-icon>
              ADD NEW PLAN
            </v-btn>
          </div>

          <!-- Empty State -->
          <v-card
            v-if="TableData.length === 0"
            class="empty-state-card"
            elevation="0"
          >
            <v-card-text class="text-center pa-10">
              <v-icon size="80" color="#ccc" class="mb-4"
                >mdi-folder-open</v-icon
              >
              <h3 class="text-h6 text-grey-1 mb-2">No plans found</h3>
              <p class="text-grey-4 mb-4">
                Start by adding a new plan for this program
              </p>
              <v-btn
                @click="createPlanFlag = true"
                color="#667eea"
                outlined
                rounded
                class="mt-4"
              >
                <v-icon small left>mdi-plus</v-icon>
                Add Plan
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Data Table -->
          <v-card
            v-if="TableData.length > 0"
            class="table-wrapper-card elevation-8"
          >
            <v-data-table
              v-model="selected"
              :headers="TableHeader"
              :items="TableData"
              :items-per-page="10"
              :search="search"
              :footer-props="{
                'items-per-page-options': [5, 10, 15, 20],
              }"
              class="modern-plans-table"
              hide-default-footer
            >
              <!-- Status Column -->
              <template v-slot:[`item.ActiveStatusTxt`]="{ item }">
                <v-chip
                  :color="getActiveStatusColor(item.ActiveStatusTxt)"
                  dark
                  class="status-chip"
                >
                  <v-icon small left>mdi-circle-medium</v-icon>
                  {{ item.ActiveStatusTxt }}
                </v-chip>
              </template>

              <!-- Content Column with View Button -->
              <template v-slot:[`item.Content`]="{ item }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="openDialog(item, 1)"
                  class="view-content-btn"
                  v-tooltip.top="'View Content'"
                >
                  <v-icon size="20">mdi-file-document-outline</v-icon>
                </v-btn>
              </template>

              <!-- Actions Column -->
              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons-group">
                  <!-- Preview Button -->
                  <v-btn
                    v-if="item.PreviewFlag"
                    icon
                    small
                    color="info"
                    @click="previewData(item, $event)"
                    class="action-icon-btn"
                    v-tooltip.top="'Preview'"
                  >
                    <v-icon size="18">mdi-eye</v-icon>
                  </v-btn>

                  <!-- Edit Button -->
                  <v-btn
                    v-if="item.EditFlag"
                    icon
                    small
                    color="warning"
                    @click="editData(item, $event)"
                    class="action-icon-btn"
                    v-tooltip.top="'Edit'"
                  >
                    <v-icon size="18">mdi-pencil</v-icon>
                  </v-btn>

                  <!-- Delete Button -->
                  <v-btn
                    v-if="item.DeleteFlag"
                    icon
                    small
                    color="error"
                    @click="deleteData(item, $event)"
                    class="action-icon-btn"
                    v-tooltip.top="'Delete'"
                  >
                    <v-icon size="18">mdi-trash-can</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <!-- Custom Footer -->
            <v-divider></v-divider>
            <v-card-text class="table-footer pa-4">
              <div class="d-flex align-center justify-space-between">
                <span class="text-caption text-grey-1">
                  Showing <strong>{{ TableData.length }}</strong> plan(s)
                </span>
                <v-pagination
                  v-model="currentPage"
                  :length="totalPages"
                  small
                  color="primary"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-sheet>

      <!-- Footer Actions -->
      <v-divider></v-divider>
      <v-card-actions class="dialog-footer pa-4">
        <v-spacer />
        <v-btn
          color="primary"
          elevation="4"
          rounded
          height="40"
          min-width="120"
          @click="closePrompt"
          class="close-dialog-btn"
        >
          <v-icon small left>mdi-close</v-icon>
          CLOSE
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import JciProgramPlansCreate from "@/view/pages/erp/jci-program/JciProgramPlansCreate.vue";
import JciProgramPlansEdit from "@/view/pages/erp/jci-program/JciProgramPlansEdit.vue";

export default {
  mixins: [common],
  components: {
    JciProgramPlansCreate,
    JciProgramPlansEdit,
  },
  props: {
    record: {
      required: true,
    },
  },
  data() {
    return {
      pageTitle: "Events Plan",

      dialog: false,
      dialogTitle: "",
      dialogContent: "",

      LoadingFlag: false,
      DeleteLoadingFlag: false,

      createPlanFlag: false,
      editPlanFlag: false,

      selected: [],
      search: "",
      selectedData: {},

      currentPage: 1,
      itemsPerPage: 10,

      TableHeader: {},
      TableOptions: {},
      TableData: [],
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.TableData.length / this.itemsPerPage);
    },
  },
  watch: {},
  created() {},
  methods: {
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideDialog", false);
    },
    openDialog(tr, id) {
      console.log("openDialog called");
      console.log({ id });
      console.log({ tr });
      var dialog = false;
      var dialogTitle="";
      var dialogContent = "";
      switch (id) {
        case 1:
          dialog = true;
          dialogTitle = tr.PlanName + " content";
          dialogContent = tr.Content;
          break;

        default:
          break;
      }
      this.dialogTitle = dialogTitle;
      this.dialogContent = dialogContent;
      this.dialog = dialog;
    },
    hideCreatePlan(flag) {
      console.log("hideCreatePlan called");
      console.log({ flag });
      this.createPlanFlag = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.editPlanFlag = true;
      } else {
        var message = "Kindly select one plan";
        this.toast("error", message);
      }
    },
    hideEditPlan(flag) {
      console.log("hideEditPlan called");
      console.log({ flag });
      this.editPlanFlag = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.getTableRecords();
    },
    getTableRecords() {
      console.log("getTableRecords is called");
      var tr = this.record;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.LoadingFlag = true;

        this.TableHeader = {};
        this.TableOptions = {};
        this.TableData = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/plan/lists";
        var upload = {
          UserInterface: 1,
          Program: tr.ProgramId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            thisIns.TableHeader = records.TableHeader;
            thisIns.TableOptions = records.TableOptions;

            if (flag == 1) {
              thisIns.TableData = records.TableData;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
              // thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "";
        if (n1 == 0) {
          message += "Event details not found. ";
        }
        this.toast("error", message);
        // this.sweetAlert("error", message, false);
      }
    },
    deleteData(tr) {
      console.log("deleteData is called");

      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      var record = this.record;

      if (n1 > 0) {
        this.DeleteLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/plan/destroy";
        var upload = {
          UserInterface: 1,
          Event: record.ProgramId,
          Plan: tr.PlanId,
          PlanName: tr.PlanName,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.DeleteLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.DeleteLoadingFlag = false;
          });
      } else {
        var message = "";
        if (n1 == 0) {
          message += "Select plan to delete. ";
        }
        // this.toast("error", message);
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    console.log("beforeMount called");
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.dialog-container {
  width: 100% !important;
  height: 100% !important;
}

/* ============================================
   DIALOG CARD
   ============================================ */
.dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  animation: dialogSlideIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

/* ============================================
   DIALOG HEADER
   ============================================ */
.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px 24px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    animation: rotate 20s linear infinite;
  }

  .header-left {
    display: flex;
    align-items: center;
    z-index: 1;
    animation: slideInLeft 0.6s ease-out;
    gap: 16px;
  }

  .header-icon-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.2);
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: pulse 2s ease-in-out infinite;
  }

  .header-text-section {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .header-right {
    display: flex;
    gap: 8px;
    z-index: 1;
  }

  .header-title {
    font-size: 1.35rem;
    font-weight: 800;
    margin: 0;
    letter-spacing: 0.5px;
    line-height: 1.2;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .header-subtitle {
    font-size: 0.8rem;
    opacity: 0.92;
    margin: 6px 0 0 0;
    font-weight: 500;
    letter-spacing: 0.2px;
    color: rgba(255, 255, 255, 0.95);
  }
}

.header-action-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &:hover {
    transform: scale(1.15) !important;
    background: rgba(255, 255, 255, 0.2) !important;
  }

  &:active {
    transform: scale(0.95) !important;
  }
}

/* ============================================
   DIALOG CONTENT
   ============================================ */
.dialog-content-sheet {
  background: linear-gradient(to bottom, #f5f7fa 0%, #ffffff 100%) !important;
  padding: 0 !important;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(102, 126, 234, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 10px;

    &:hover {
      background: rgba(102, 126, 234, 0.5);
    }
  }
}

.dialog-body-content {
  padding: 24px !important;
  animation: fadeIn 0.6s ease-out 0.2s both;
}

/* ============================================
   ADD BUTTON SECTION
   ============================================ */
.add-button-section {
  display: flex;
  justify-content: flex-start;

  .add-plan-btn {
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.25) !important;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4) !important;
    }

    &:active {
      transform: translateY(0);
    }
  }
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-state-card {
  border-radius: 12px !important;
  border: 2px dashed rgba(102, 126, 234, 0.2) !important;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.02) 0%,
    rgba(118, 75, 162, 0.02) 100%
  ) !important;
  animation: fadeIn 0.6s ease-out;

  .v-card__text {
    padding: 40px 20px !important;

    .v-icon {
      animation: float 4s ease-in-out infinite;
    }

    h3 {
      color: #667eea;
      font-weight: 700;
      margin-bottom: 8px;
    }

    p {
      color: #999;
      font-size: 14px;
    }
  }
}

/* ============================================
   TABLE WRAPPER
   ============================================ */
.table-wrapper-card {
  border-radius: 12px !important;
  overflow: hidden;
  animation: slideInUp 0.6s ease-out;

  ::v-deep .v-data-table {
    background: transparent !important;

    &__wrapper {
      table {
        border-collapse: collapse;

        thead {
          tr {
            background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);

            th {
              background: transparent !important;
              color: white !important;
              font-weight: 700 !important;
              font-size: 13px !important;
              padding: 16px 12px !important;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: none !important;
            }
          }
        }

        tbody {
          tr {
            border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

            &:hover {
              background: linear-gradient(
                90deg,
                rgba(102, 126, 234, 0.05) 0%,
                rgba(118, 75, 162, 0.05) 100%
              ) !important;
              transform: scale(1.005);
              box-shadow: inset 0 2px 8px rgba(102, 126, 234, 0.05);
            }

            td {
              padding: 14px 12px !important;
              color: #333;
              font-size: 13px;
            }

            &:last-child {
              border-bottom: none !important;
            }
          }
        }
      }
    }
  }
}

.modern-plans-table {
  ::v-deep {
    .v-data-table__wrapper {
      border-radius: 8px;
    }
  }
}

/* ============================================
   TABLE FOOTER
   ============================================ */
.table-footer {
  background: linear-gradient(90deg, #f9fafb 0%, #ffffff 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  padding: 12px 24px !important;

  .v-pagination {
    ::v-deep .v-pagination__item {
      &:hover {
        background: rgba(102, 126, 234, 0.1);
      }
    }
  }
}

/* ============================================
   STATUS CHIP
   ============================================ */
.status-chip {
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
  }
}

/* ============================================
   ACTION BUTTONS
   ============================================ */
.action-buttons-group {
  display: flex;
  gap: 6px;
  justify-content: center;
}

.action-icon-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 6px !important;

  &:hover {
    transform: scale(1.25) rotate(5deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.9);
  }
}

.view-content-btn {
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.3);
  }
}

/* ============================================
   INFO DIALOG
   ============================================ */
.info-dialog-card {
  border-radius: 12px !important;
  overflow: hidden;
  animation: fadeIn 0.4s ease-out;

  .info-dialog-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white !important;
    font-weight: 700;
    padding: 16px 20px !important;
  }

  .info-dialog-content {
    max-height: 300px;
    overflow-y: auto;
    padding: 16px !important;

    p {
      line-height: 1.8;
      color: #333;
    }
  }

  .close-info-btn {
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
}

/* ============================================
   DIALOG FOOTER
   ============================================ */
.dialog-footer {
  background: linear-gradient(90deg, #f9fafb 0%, #ffffff 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  padding: 16px 24px !important;

  .close-dialog-btn {
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2) !important;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3) !important;
    }

    &:active {
      transform: translateY(0);
    }
  }
}

/* ============================================
   ANIMATIONS
   ============================================ */
@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(20px);
  }

  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  50% {
    box-shadow: 0 4px 20px rgba(255, 255, 255, 0.3);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   RESPONSIVE DESIGN
   ============================================ */
@media (max-width: 960px) {
  .dialog-header {
    flex-direction: column;
    text-align: center;
    align-items: center !important;

    .header-left {
      justify-content: center;
      margin-bottom: 12px;
    }

    .header-right {
      width: 100%;
      justify-content: center;
    }

    .header-title {
      font-size: 1.1rem;
    }

    .header-subtitle {
      font-size: 0.8rem;
    }
  }

  .dialog-body-content {
    padding: 16px !important;
  }

  .add-button-section {
    .add-plan-btn {
      width: 100%;
    }
  }
}

@media (max-width: 600px) {
  .dialog-card {
    max-height: 95vh;
    max-width: 95vw;
  }

  .dialog-header {
    padding: 16px !important;

    .header-left {
      flex-direction: column;
      text-align: center;
    }

    .header-icon {
      margin-bottom: 8px;
    }

    .header-title {
      font-size: 1rem;
    }

    .header-subtitle {
      display: none;
    }

    .header-right {
      margin-top: 8px;
    }
  }

  .dialog-body-content {
    padding: 12px !important;
  }

  .action-buttons-group {
    gap: 4px;
  }

  .action-icon-btn {
    ::v-deep .v-icon {
      font-size: 16px !important;
    }
  }

  .table-footer {
    padding: 8px 12px !important;
  }
}

/* ============================================
   ADDITIONAL STYLING
   ============================================ */
::v-deep .v-tooltip__content {
  background: rgba(0, 0, 0, 0.9) !important;
  border-radius: 6px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

::v-deep .v-card {
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.1) !important;
}

/* Custom scrollbar for the entire dialog */
.dialog-container {
  ::v-deep ::-webkit-scrollbar {
    width: 8px;
  }

  ::v-deep ::-webkit-scrollbar-track {
    background: rgba(102, 126, 234, 0.05);
    border-radius: 10px;
  }

  ::v-deep ::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 10px;

    &:hover {
      background: rgba(102, 126, 234, 0.5);
    }
  }
}
</style>